import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  Revision,
  CreateRevisionInput,
  RevisionsListResponse,
} from '@/types/revision'

// Query key factory
export const revisionKeys = {
  all: ['revisions'] as const,
  lists: () => [...revisionKeys.all, 'list'] as const,
  list: (resource: string, recordId: number) =>
    [...revisionKeys.lists(), { resource, recordId }] as const,
}

/**
 * Hook to fetch revisions for a specific resource and record
 * Used to get revision history for an assistant
 */
export function useRevisions(
  resource: string,
  recordId: number | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: revisionKeys.list(resource, recordId!),
    queryFn: async (): Promise<Revision[]> => {
      // Build query params in react-admin format
      const params = new URLSearchParams()
      params.set('sort', JSON.stringify(['date', 'DESC']))
      params.set('range_', JSON.stringify([0, 99])) // Get up to 100 revisions
      params.set(
        'filter',
        JSON.stringify({
          resource: resource,
          record_id: recordId,
        })
      )

      // Use react-admin endpoint format (POST to /ra/getList)
      const response = await api.post<RevisionsListResponse>(
        `/revisions/ra/getList?${params.toString()}`
      )

      // Response from backend includes data array with revisions
      // Backend transforms to camelCase (recordId, authorId)
      return response.data.data || []
    },
    enabled: enabled && !!recordId,
    staleTime: 30 * 1000, // Cache for 30 seconds
  })
}

/**
 * Hook to create a new revision
 * Called when saving an assistant to capture the previous state
 */
export function useCreateRevision() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateRevisionInput) => {
      // Use react-admin endpoint format (POST to /ra/create)
      const response = await api.post('/revisions/ra/create', {
        resource: input.resource,
        record_id: input.record_id,
        data: input.data,
        author_id: input.author_id,
        date: new Date().toISOString(),
        message: input.message,
        description: input.description,
      })
      return response.data.data || response.data
    },
    onSuccess: (data, variables) => {
      // Invalidate revisions list for this resource/record
      queryClient.invalidateQueries({
        queryKey: revisionKeys.list(variables.resource, variables.record_id),
      })
    },
  })
}

/**
 * Hook to delete a specific revision
 */
export function useDeleteRevision() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      resource,
      recordId,
    }: {
      id: number
      resource: string
      recordId: number
    }) => {
      // Use react-admin endpoint format (POST to /ra/delete/{id})
      await api.post(`/revisions/ra/delete/${id}`)
      return { id, resource, recordId }
    },
    onSuccess: (data) => {
      // Invalidate revisions list for this resource/record
      queryClient.invalidateQueries({
        queryKey: revisionKeys.list(data.resource, data.recordId),
      })
    },
  })
}

/**
 * Utility to generate a human-readable commit message from changed fields
 */
export function generateRevisionMessage(
  changedFields: string[],
  isRevert = false
): string {
  if (isRevert) {
    return 'Reverted to previous version'
  }

  if (changedFields.length === 0) {
    return 'Updated configuration'
  }

  if (changedFields.length === 1) {
    return `Updated ${formatFieldName(changedFields[0])}`
  }

  if (changedFields.length <= 3) {
    const formatted = changedFields.map(formatFieldName)
    return `Updated ${formatted.slice(0, -1).join(', ')} and ${formatted.slice(-1)}`
  }

  return `Updated ${changedFields.length} fields`
}

/**
 * Format a field path to a human-readable name
 */
function formatFieldName(field: string): string {
  // Handle nested paths like prompt_scenarios.faq
  const fieldName = field.includes('.') ? field.split('.').pop()! : field

  // Convert snake_case to Title Case
  return fieldName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Utility to detect which fields changed between two objects
 */
export function getChangedFields(
  previous: Record<string, unknown>,
  current: Record<string, unknown>
): string[] {
  const changedFields: string[] = []

  const allKeys = new Set([...Object.keys(previous), ...Object.keys(current)])

  for (const key of allKeys) {
    // Skip internal fields
    if (['id', 'created_at', 'updated_at', 'deleted_at'].includes(key)) {
      continue
    }

    const prevValue = previous[key]
    const currValue = current[key]

    // Deep comparison for objects
    if (!deepEqual(prevValue, currValue)) {
      changedFields.push(key)
    }
  }

  return changedFields
}

/**
 * Deep equality check for two values
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a === null || b === null) return a === b
  if (typeof a !== typeof b) return false

  if (typeof a === 'object') {
    const aObj = a as Record<string, unknown>
    const bObj = b as Record<string, unknown>

    const aKeys = Object.keys(aObj)
    const bKeys = Object.keys(bObj)

    if (aKeys.length !== bKeys.length) return false

    for (const key of aKeys) {
      if (!deepEqual(aObj[key], bObj[key])) return false
    }
    return true
  }

  return false
}
